import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// ==================== Initial State ====================
const initialState = {
  injections: [],
  currentInjection: null,
  isLoading: false,
  error: null,
};

// ==================== Async Thunks ====================

// Fetch all injections (optionally by patient ID)
export const fetchInjections = createAsyncThunk(
  'injections/fetchInjections',
  async (patientId = null, { rejectWithValue }) => {
    try {
      const url = patientId
        ? `/injections/${patientId}`
        : '/injections';
      const response = await api.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch injections'
      );
    }
  }
);

// Fetch single injection by ID
export const fetchInjection = createAsyncThunk(
  'injections/fetchInjection',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/injections/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch injection'
      );
    }
  }
);

// Create new injection
export const createInjection = createAsyncThunk(
  'injections/createInjection',
  async (injectionData, { rejectWithValue }) => {
    try {
      const response = await api.post('/injections', injectionData);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to create injection'
      );
    }
  }
);

// Update injection by caseId and patientId
export const updateInjectionByCase = createAsyncThunk(
  'injections/updateInjectionByCase',
  async ({ caseId, patientId, injectionData }, { rejectWithValue }) => {
    try {
      const response = await api.put(
        `/injections/case/${caseId}/patient/${patientId}`,
        injectionData
      );
      return response.data.data ?? response.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to update injection by case'
      );
    }
  }
);

// Delete injection
export const deleteInjection = createAsyncThunk(
  'injections/deleteInjection',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/injections/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to delete injection'
      );
    }
  }
);

// ==================== Slice ====================
const injectionsSlice = createSlice({
  name: 'injections',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentInjection: (state, action) => {
      state.currentInjection = action.payload;
    },
    clearCurrentInjection: (state) => {
      state.currentInjection = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // FETCH ALL
      .addCase(fetchInjections.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchInjections.fulfilled, (state, action) => {
        state.isLoading = false;
        state.injections = action.payload;
      })
      .addCase(fetchInjections.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // FETCH SINGLE
      .addCase(fetchInjection.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchInjection.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentInjection = action.payload;
      })
      .addCase(fetchInjection.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // CREATE
      .addCase(createInjection.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createInjection.fulfilled, (state, action) => {
        state.isLoading = false;
        state.injections.unshift(action.payload);
      })
      .addCase(createInjection.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // UPDATE BY CASE
      .addCase(updateInjectionByCase.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateInjectionByCase.fulfilled, (state, action) => {
        state.isLoading = false;
        const updated = action.payload.data ?? action.payload;

        // Find existing record by id
        const index = state.injections.findIndex(
          (i) => i.id === updated.id
        );
        if (index !== -1) {
          state.injections[index] = updated;
        }

        // If currentInjection matches, update it too
        if (state.currentInjection?.id === updated.id) {
          state.currentInjection = updated;
        }
      })
      .addCase(updateInjectionByCase.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DELETE
      .addCase(deleteInjection.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteInjection.fulfilled, (state, action) => {
        state.isLoading = false;
        state.injections = state.injections.filter(
          (i) => i.injectionPatientInfo.id !== action.payload
        );
        if (
          state.currentInjection?.injectionPatientInfo.id ===
          action.payload
        ) {
          state.currentInjection = null;
        }
      })
      .addCase(deleteInjection.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, setCurrentInjection, clearCurrentInjection } =
  injectionsSlice.actions;

export default injectionsSlice.reducer;
